import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { companyOf, DEFAULT_COMPANY } from '../utils/company';
import {
  Calendar, Receipt, FileSignature, Gift, MapPin, Check, Share2, CreditCard,
  ShieldCheck, Star, ChevronRight, ChevronDown, MessageSquare, Camera, X, Leaf, Phone, HelpCircle,
} from 'lucide-react';
import { publicApi, SERVER_ORIGIN } from '../services/api';
import { money, longDate, shortDate, tinyDate, clock } from '../utils/format';
import { PublicLoading, DeadLink, PublicFooter, H1, ButtonPrimary, ButtonGhost } from '../components/public';
import LawnWalkthroughButton from '../components/LawnWalkthroughButton';
import BusinessPortal from './BusinessPortal';

/**
 * The customer's own page. A private link, no password.
 *
 * Rebuilt Sep 19 2026. Overview used to be eleven cards of equal weight, and
 * the two questions people actually open it for — when are you coming, and
 * what do I owe — were a sentence and the fourth card down. Now they are the
 * whole first screen, with Pay on it. Everything that is a *service* (lawn
 * plans, one-off jobs, treatment offers) moved to the Services tab, which is
 * what that tab is for and what was making Overview so long.
 *
 * Nothing about how anything works changed: same endpoints, same sheets, same
 * setup guide, same rewards.
 */

/**
 * Kept for the sheets further down, which pass their own Intl options.
 * Everything new should use the shared helpers in utils/format.
 */
const day = (iso?: string | null, opts: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'long', day: 'numeric' }) =>
  iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', opts) : '';

/**
 * The card every section of this page is built from.
 *
 * Kept on the same props as before so the sheets and tabs below need no
 * changes — only the skin moved to the design system: heavier heading, bigger
 * radius, and body text that is never lighter than gray-700.
 *
 * `id` is so the setup guide can send someone straight to the right card
 * instead of dropping them at the top of a tab.
 */
function Card({ title, icon: Icon, children, tone = 'plain', id }: {
  title?: string; icon?: React.ElementType; children: React.ReactNode; tone?: 'plain' | 'brand' | 'green'; id?: string;
}) {
  return (
    <section id={id} className={`rounded-3xl border p-5 shadow-soft ${tone === 'brand' ? 'border-brand-300 bg-brand-100'
      : tone === 'green' ? 'border-emerald-300 bg-emerald-50' : 'border-brand-100 bg-white'}`}>
      {title && (
        <h2 className="text-base font-black uppercase tracking-tight mb-3 flex items-center gap-2 text-gray-900">
          {Icon && <Icon className="h-5 w-5 text-brand-600" aria-hidden />} {title}
        </h2>
      )}
      {children}
    </section>
  );
}

export default function CustomerPortal() {
  const { token } = useParams<{ token: string }>();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<'overview' | 'services' | 'settings'>(() => {
    const q = new URLSearchParams(window.location.search);
    // ?card=save — the office texted them here to save a card: straight to Settings, at the card.
    if (q.get('card') === 'save') return 'settings';
    const t = q.get('tab');
    return t === 'settings' || t === 'services' ? t : 'overview';
  });
  const [goCard, setGoCard] = useState(() => new URLSearchParams(window.location.search).get('card') === 'save');
  const [texting, setTexting] = useState(false);
  const [helping, setHelping] = useState(false);
  const [caring, setCaring] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery(
    ['portal', token],
    () => publicApi.get(`/portal/${token}`).then(r => r.data),
    { enabled: !!token, retry: false },
  );
  const company = companyOf(data);

  // Stripe sends them back with ?card=saved — confirm it really saved.
  useEffect(() => {
    if (params.get('card') === 'saved' && token) {
      publicApi.post(`/portal/${token}/card/confirm`).then(() => { refetch(); setTab('settings'); });
    }
    if (params.get('card')) { params.delete('card'); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Once their page is up, scroll to "Credit card on file" (the link said ?card=save).
  useEffect(() => {
    if (!goCard || !data) return;
    setGoCard(false);
    setTimeout(() => document.getElementById('portal-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
  }, [goCard, data]);

  if (isLoading) return <PublicLoading />;
  if (error || !data) return <DeadLink what="account" company={company} />;
  // A business reads a business's page: sites, visits with their reports, bills on terms, papers (Oct 20 2026).
  if (data.business) return <BusinessPortal data={data} refetch={refetch} />;

  const { customer, upcoming, recent, invoices, quotes, rewards } = data;
  // "To pay" = only invoices actually sent to them (server sets `payable`).
  // Drafts are internal bookkeeping and must never show a Pay CTA. Older API
  // responses without the flag fall back to the sent/overdue rule.
  const owing = invoices.filter((i: any) =>
    i.payable != null ? i.payable : (i.status === 'sent' || i.status === 'overdue'));
  const settled = invoices.filter((i: any) => !owing.includes(i) && i.status !== 'draft');
  const next = upcoming[0];
  const firstName = customer.first_name || String(customer.name || '').split(' ')[0];
  const owedTotal = owing.reduce((s: number, i: any) => s + (Number(i.total) || 0), 0);
  const needsSignature = quotes.filter((q: any) => q.needs_signature);
  const TABS: { key: 'overview' | 'services' | 'settings'; label: string }[] = [
    { key: 'overview', label: 'Home' },
    { key: 'services', label: 'Services' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-brand-50 flex flex-col">
      <header className="bg-brand-600 text-black sticky top-0 z-30 shadow-brand">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-[72px] flex items-center gap-3">
          <span className="flex-shrink-0 h-11 w-11 rounded-2xl bg-white/15 flex items-center justify-center">
            <img src="/logo-white.png" alt="" width={32} height={32} className="h-8 w-8 object-contain" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-black uppercase tracking-tight leading-none text-[14px] whitespace-nowrap">Keep Austin Mowed</p>
          </div>
          <button onClick={() => setTexting(true)}
            className="press flex-shrink-0 inline-flex items-center gap-1.5 bg-white text-brand-700 px-3.5 h-11 rounded-2xl shadow-soft text-[11px] font-black uppercase tracking-wide">
            <MessageSquare className="h-4 w-4" aria-hidden /> Message
          </button>
        </div>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 flex gap-1 border-t border-white/20">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} aria-current={tab === t.key ? 'page' : undefined}
              className={`press px-4 min-h-[50px] text-[15px] font-black uppercase tracking-tight border-b-[3px] ${tab === t.key ? 'border-white text-white' : 'border-transparent text-white/60'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 sm:px-6 py-5 space-y-4">
        {tab === 'overview' ? (
          <>
            {/* The two questions people open this page to answer. */}
            <section className="bg-white rounded-3xl border border-brand-100 shadow-lift overflow-hidden">
              <div className="px-5 pt-5 pb-1">
                <H1>Hi {firstName}</H1>
              </div>
              <div className="px-5 py-6 mt-3 bg-brand-600 text-black">
                <p className="text-[13px] font-black uppercase tracking-wide text-black/70">Your next visit</p>
                {next ? (
                  <>
                    <p className="text-2xl font-black tracking-tight mt-1">{longDate(next.date)}</p>
                    <p className="text-[15px] font-medium text-black/80 mt-1">
                      {next.time ? `Around ${clock(next.time)}.` : next.window ? `In the ${next.window}.` : ''}
                      {' '}We text you the morning of your visit.
                    </p>
                  </>
                ) : (
                  <p className="text-[17px] font-medium text-black/80 mt-1">Nothing on the schedule right now. Text us and we will get you booked in.</p>
                )}
              </div>
              {owing.length > 0 && (
                <div className="px-5 py-5 border-t-2 border-brand-100">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[15px] font-black uppercase tracking-tight text-gray-900">
                      {owing.length === 1 ? 'Amount due' : `${owing.length} invoices to pay`}
                    </span>
                    <span className="text-3xl font-black tracking-tight text-brand-600 tabular-nums">{money(owedTotal)}</span>
                  </div>
                  {owing.length === 1 && owing[0].due_date && (
                    <p className="text-[15px] text-gray-700 mt-1">Due by {longDate(owing[0].due_date)}</p>
                  )}
                  <div className="mt-4 space-y-2">
                    {owing.map((i: any) => (
                      <ButtonPrimary key={i.id} href={i.link || '#'}>
                        <CreditCard className="h-5 w-5" aria-hidden />
                        Pay {money(i.total)}{owing.length > 1 ? ` · ${i.number}` : ''}
                      </ButtonPrimary>
                    ))}
                  </div>
                  {owing.some((i: any) => i.discount) && (
                    <p className="text-[14px] font-semibold text-emerald-700 mt-2">
                      {owing.find((i: any) => i.discount)?.discount.label} is already off this one.
                    </p>
                  )}
                </div>
              )}
            </section>

            {needsSignature.length > 0 && (
              <Card title="Waiting on you" icon={FileSignature} tone="brand">
                <ul className="space-y-2">
                  {needsSignature.map((q: any) => (
                    <li key={q.id}>
                      <a href={q.link || '#'} className="press flex items-center gap-3 rounded-2xl border-2 border-brand-300 bg-white shadow-soft px-4 py-4 min-h-[64px]">
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-bold text-gray-900">{q.title}</p>
                          <p className="text-[14px] text-gray-700 tabular-nums">{money(q.total)} · needs your signature</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden />
                      </a>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <SetupGuide token={token!} setup={data?.setup} properties={data?.properties || []}
              onChange={refetch} onGoSettings={(action?: string) => {
                /**
                 * Send them where the thing actually IS. A phone or email step
                 * used to drop them on Settings with nothing open (owner, Sep 21
                 * 2026: "the customer tutorial is not working like I want").
                 */
                setTab('settings');
                if (action === 'contact') setOpenContact(true);
                if (action === 'card') setTimeout(() => document.getElementById('portal-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
                if (action === 'rewards') setTimeout(() => document.getElementById('portal-rewards')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
              }} />

            <Card title="Coming up" icon={Calendar}>
              {upcoming.length === 0 ? (
                <p className="text-[15px] text-gray-700">Nothing booked yet.</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {upcoming.map((j: any) => (
                    <li key={j.id} className="py-3 first:pt-0 last:pb-0 flex items-baseline justify-between gap-3">
                      <span className="text-[15px] font-bold text-gray-900 w-28 flex-shrink-0">{shortDate(j.date)}</span>
                      <span className="flex-1 text-[15px] text-gray-800">
                        {j.service}{j.time ? ` · ${clock(j.time)}` : j.window ? ` · ${j.window}` : ''}{j.in_progress ? ' · here now' : ''}
                      </span>
                      {j.price > 0 && <span className="text-[15px] text-gray-700 tabular-nums">{money(j.price)}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {recent.length > 0 && <RecentWork recent={recent} />}

            <RewardsSummary rewards={rewards} onOpenSettings={() => setTab('settings')} />

            {settled.length > 0 && (
              <section className="bg-white rounded-3xl border border-brand-100 shadow-soft overflow-hidden">
                <button type="button" onClick={() => setPastOpen(v => !v)} aria-expanded={pastOpen}
                  className="press w-full flex items-center justify-between gap-3 px-5 py-4 min-h-[60px] text-left">
                  <span className="flex items-center gap-2 text-base font-black uppercase tracking-tight text-gray-900">
                    <Receipt className="h-5 w-5 text-brand-600" aria-hidden /> Past invoices
                  </span>
                  <ChevronDown className={`h-5 w-5 text-gray-700 flex-shrink-0 transition-transform ${pastOpen ? 'rotate-180' : ''}`} aria-hidden />
                </button>
                {pastOpen && (
                  <ul className="px-5 pb-5 divide-y divide-gray-200">
                    {settled.slice(0, 12).map((i: any) => (
                      <li key={i.id}>
                        <a href={i.link || '#'} className="press py-3 flex items-center gap-3 min-h-[56px]">
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] text-gray-900">
                              <span className="font-bold tabular-nums">{money(i.total)}</span>
                              <span className="text-gray-700"> · {i.number}{i.paid_at ? ` · paid ${tinyDate(String(i.paid_at).slice(0, 10))}` : i.status === 'paid' ? ' · paid' : ''}</span>
                            </p>
                            {i.discount && <p className="text-[14px] text-emerald-700 font-semibold">{i.discount.label}: &minus;{money(i.discount.amount)}</p>}
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </>
        ) : tab === 'services' ? (
          <>
            {/* Plans are sold by the office, a bid, or the flyer, not from this page (owner, Sep 20 2026). */}
            {data?.treatment_program && (
              <TreatmentProgram token={token!} program={data.treatment_program} onChange={refetch} />
            )}
            <TreatmentOffers token={token!} offers={data?.treatments || []} onChange={refetch} />
            <ServicesTab token={token!} data={data} onChange={refetch} />
          </>
        ) : (
          <SettingsTab token={token!} data={data} onChange={refetch} openContact={openContact} onContactOpened={() => setOpenContact(false)} />
        )}

        {/* The free lawn walkthrough (owner, Oct 20 2026): outlined, never the page's filled button. ?walk=1 opens it (the office's link). */}
        <div className="pt-2">
          <LawnWalkthroughButton kind="portal" token={token!} autoOpen={params.get('walk') === '1'} />
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2">
          <ButtonGhost onClick={() => setHelping(true)} className="!text-[13px] !px-3">
            <HelpCircle className="h-5 w-5" aria-hidden /> Help
          </ButtonGhost>
          <ButtonGhost onClick={() => setCaring(true)} className="!text-[13px] !px-3">Something wrong?</ButtonGhost>
        </div>
      </main>

      <PublicFooter company={company} />

      {texting && <MessageSheet token={token!} onClose={() => setTexting(false)} />}
      {helping && <HelpSheet company={company} onClose={() => setHelping(false)} onMessage={() => { setHelping(false); setTexting(true); }} />}
      {caring && <CareSheet token={token!} data={data} onClose={() => setCaring(false)} />}
    </div>
  );
}

function CareSheet({ token, data, onClose }: { token: string; data: any; onClose: () => void }) {
  const kinds: any[] = data?.care_kinds || [];
  const recent: any[] = (data?.recent || []).slice(0, 5);
  const [kind, setKind] = useState('');
  const [jobId, setJobId] = useState('');
  const [detail, setDetail] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    try {
      // A photo goes as a form; without one, plain JSON is fine.
      let r: any;
      if (photo) {
        const form = new FormData();
        form.append('kind', kind); form.append('detail', detail);
        if (jobId) form.append('job_id', jobId);
        form.append('photo', photo);
        r = (await publicApi.post(`/portal/${token}/care`, form)).data;
      } else {
        r = (await publicApi.post(`/portal/${token}/care`, {
          kind, detail, job_id: jobId ? Number(jobId) : undefined,
        })).data;
      }
      setSent(r.message);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Sorry, that did not send. Please ring us.');
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4 max-h-[88vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-bold text-gray-900">Tell us what happened</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        {sent ? (
          <>
            <p className="text-sm text-gray-700">{sent}</p>
            <button onClick={onClose} className="press w-full py-3 rounded-xl bg-brand-600 text-black font-semibold">Close</button>
          </>
        ) : (
          <>
            <div className="space-y-1.5">
              {kinds.map(k => (
                <button key={k.key} onClick={() => setKind(k.key)}
                  className={`w-full text-left px-3.5 py-2.5 rounded-xl border text-sm ${
                    kind === k.key ? 'border-brand-400 bg-brand-50 font-medium text-brand-900' : 'border-gray-200 text-gray-700'}`}>
                  {k.label}
                </button>
              ))}
            </div>

            {recent.length > 0 && (
              <label className="block">
                <span className="text-xs font-medium text-gray-600">Which visit? (optional)</span>
                <select value={jobId} onChange={e => setJobId(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm">
                  <option value="">Not about one visit</option>
                  {recent.map(j => <option key={j.id} value={j.id}>{day(j.date)}, {j.service || 'visit'}</option>)}
                </select>
              </label>
            )}

            <label className="block">
              <span className="text-xs font-medium text-gray-600">What happened?</span>
              <textarea value={detail} onChange={e => setDetail(e.target.value)} rows={4}
                placeholder="The strip by the fence was left long, and the side gate was open when I got home."
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">Add a photo (optional)</span>
              <input type="file" accept="image/*" capture="environment"
                onChange={e => setPhoto(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-brand-700" />
              {photo && <span className="text-[11px] text-gray-500">{photo.name}</span>}
            </label>

            <button onClick={send} disabled={busy || !kind || detail.trim().length < 4}
              className="press w-full py-3.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-40">
              {busy ? 'Sending…' : 'Send it'}
            </button>
            <p className="text-[11px] text-gray-500 text-center">
              This goes straight to Michael, not to a queue.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Extras, chosen by the person who actually wants them.
 *
 * The crew is coming anyway. Ticking something here does not book it or charge
 * for it — it tells the office, and the office confirms the price. Saying that
 * plainly is better than a basket that looks like a checkout and is not one.
 */
function ServicesTab({ token, data, onChange }: { token: string; data: any; onChange: () => void }) {
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [request, setRequest] = useState('');
  const [busy, setBusy] = useState(false);
  const catalogue: any[] = data?.add_ons || [];
  const requests: any[] = data?.requests || [];
  const open = requests.filter(r => r.status === 'requested');
  const settled = requests.filter(r => r.status !== 'requested');
  const next = (data?.upcoming || [])[0];

  const toggle = (k: string) => setPicked(v => v.includes(k) ? v.filter(x => x !== k) : [...v, k]);

  async function send() {
    if (!picked.length) return;
    setBusy(true);
    try {
      const { data: r } = await publicApi.post(`/portal/${token}/services`, { services: picked, note: note.trim() || undefined });
      setPicked([]); setNote('');
      onChange();
      alert(r.message);
    } catch {
      alert('Sorry, that did not save. Please try again.');
    } finally { setBusy(false); }
  }

  // The plain box: whatever they need, in their own words. Michael prices it.
  async function sendRequest() {
    if (request.trim().length < 4) return;
    setBusy(true);
    try {
      const { data: r } = await publicApi.post(`/portal/${token}/services`, { request: request.trim() });
      setRequest('');
      onChange();
      alert(r.message);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Sorry, that did not send. Please try again.');
    } finally { setBusy(false); }
  }

  async function cancel(id: number) {
    setBusy(true);
    try { await publicApi.post(`/portal/${token}/services/${id}/cancel`); onChange(); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Request a service</h1>
        <p className="text-sm text-gray-600 mt-1">
          {next
            ? <>We're out on <span className="font-semibold text-gray-900">{day(next.date)}</span>. Tell us what you need and we'll price it before then.</>
            : <>Tell us what you need and we'll come back with a price.</>}
        </p>
      </div>

      <Card title="What would you like done?" icon={Leaf}>
        <textarea value={request} onChange={e => setRequest(e.target.value)} rows={4}
          placeholder="e.g. Trim the crepe myrtles out front, and can you clear the leaves from the back beds?"
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
        <button onClick={sendRequest} disabled={busy || request.trim().length < 4}
          className="press mt-2 w-full py-3.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-40">
          {busy ? 'Sending…' : 'Send request'}
        </button>
        <p className="mt-2 text-[11px] text-gray-500 text-center">Nothing is booked or charged until Michael confirms the price with you.</p>
      </Card>

      {open.length > 0 && (
        <Card title="You've asked for" icon={Check} tone="green">
          <ul className="space-y-2">
            {open.map(r => (
              <li key={r.id} className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">{r.name}</p>
                  <p className="text-xs text-gray-500">
                    asked {day(r.asked_on)}{r.visit_date ? ` · for ${day(r.visit_date)}` : ''} · we'll confirm the price
                  </p>
                </div>
                <button onClick={() => cancel(r.id)} disabled={busy}
                  className="text-xs text-gray-500 hover:text-red-600 disabled:opacity-50">Remove</button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {catalogue.length > 0 && (
      <Card title="What we can do" icon={Leaf}>
        <ul className="space-y-2">
          {catalogue.map(s => {
            const already = open.some(r => r.service_key === s.key);
            const on = picked.includes(s.key);
            return (
              <li key={s.key}>
                <button onClick={() => !already && toggle(s.key)} disabled={already}
                  className={`w-full text-left rounded-xl border p-3.5 transition ${
                    already ? 'border-gray-100 bg-gray-50 opacity-60'
                    : on ? 'border-brand-400 bg-brand-50' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">{s.name}</p>
                      <p className="text-sm text-gray-600 mt-0.5">{s.detail}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="font-bold text-gray-900 whitespace-nowrap">{s.price_label}</span>
                      {already && <p className="text-[11px] text-gray-500 mt-1">already asked</p>}
                      {!already && on && <p className="text-[11px] text-brand-600 font-medium mt-1">added</p>}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </Card>
      )}

      {picked.length > 0 && (
        <div className="space-y-2">
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
            placeholder="Anything we should know? (optional)"
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
          <button onClick={send} disabled={busy}
            className="press w-full py-3.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
            {busy ? 'Sending…' : `Ask for ${picked.length === 1 ? 'this' : `these ${picked.length}`}`}
          </button>
          <p className="text-[11px] text-gray-500 text-center">
            Nothing is charged yet. We confirm the price with you first.
          </p>
        </div>
      )}

      {settled.length > 0 && (
        <Card title="Before">
          <ul className="space-y-1.5 text-sm">
            {settled.map(r => (
              <li key={r.id} className="flex justify-between text-gray-600">
                <span>{r.name} <span className="text-xs text-gray-500">· {r.status}</span></span>
                {r.price != null && <span>${r.price.toFixed(2)}</span>}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

/**
 * Something the crew noticed in the yard and thought was worth treating.
 *
 * It is an offer, not a charge — nothing happens until they say yes, and the
 * price they see here is the price.
 */
function TreatmentOffers({ token, offers, onChange }: { token: string; offers: any[]; onChange: () => void }) {
  const [busy, setBusy] = useState<number | null>(null);
  const open = offers.filter(o => o.status === 'offered');
  const booked = offers.filter(o => o.status === 'accepted' || o.status === 'scheduled');
  if (!open.length && !booked.length) return null;

  async function answer(id: number, yes: boolean) {
    setBusy(id);
    try {
      await publicApi.post(`/portal/${token}/treatment/${id}`, { answer: yes ? 'yes' : 'no' });
      onChange();
    } finally { setBusy(null); }
  }

  return (
    <Card title="Your lawn could use" icon={Leaf} tone="green">
      <ul className="space-y-3">
        {open.map(o => (
          <li key={o.id} className="rounded-xl border border-emerald-200 bg-white p-3.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900">{o.treatment}</p>
                <p className="text-xs text-emerald-800 mt-0.5">{o.issue}</p>
              </div>
              <span className="font-bold text-gray-900 whitespace-nowrap">${o.price.toFixed(2)}</span>
            </div>
            {o.detail && <p className="text-sm text-gray-600 mt-2">{o.detail}</p>}
            <div className="flex gap-2 mt-3">
              <button onClick={() => answer(o.id, true)} disabled={busy === o.id}
                className="flex-1 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">
                {busy === o.id ? 'One moment…' : 'Yes, please'}
              </button>
              <button onClick={() => answer(o.id, false)} disabled={busy === o.id}
                className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium disabled:opacity-50">
                No thanks
              </button>
            </div>
          </li>
        ))}
        {booked.map(o => (
          <li key={o.id} className="rounded-xl border border-gray-200 bg-white p-3.5 flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-gray-900">{o.treatment}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {o.status === 'scheduled' ? "Booked. We will see you then." : "You said yes. We will be in touch to book it."}
              </p>
            </div>
            <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">${o.price.toFixed(2)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Finished visits, with the crew's photos of the yard. */
function RecentWork({ recent }: { recent: any[] }) {
  const [zoom, setZoom] = useState<string | null>(null);
  return (
    <Card title="Recent visits" icon={Camera}>
      <ul className="space-y-3">
        {recent.map((j: any) => (
          <li key={j.id}>
            <div className="flex items-baseline gap-3 text-[15px]">
              <span className="font-bold text-gray-900 w-28 flex-shrink-0">{day(j.date, { month: 'short', day: 'numeric' })}</span>
              <span className="flex-1 text-gray-800 capitalize">{j.service}</span>
              {j.price > 0 && <span className="text-gray-700 tabular-nums">{money(j.price)}</span>}
            </div>
            {j.photos?.length > 0 && (
              <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                {j.photos.map((p: string) => (
                  <button key={p} onClick={() => setZoom(`${SERVER_ORIGIN}${p}`)} className="flex-shrink-0">
                    <img src={`${SERVER_ORIGIN}${p}`} alt="Your yard" loading="lazy"
                      className="h-24 w-32 object-cover rounded-lg border border-gray-200" />
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setZoom(null)}>
          <img src={zoom} alt="Your yard" className="max-h-full max-w-full rounded-lg" />
        </div>
      )}
    </Card>
  );
}

/** The share is a link (with the code in it), not a bare code to retype. */
function shareMessage(rewards: any) {
  const code = String(rewards?.referral_code || '');
  const url = rewards?.referral_link || `${window.location.origin}/r/${code}`;
  return { url, text: `I use Keep Austin Mowed for my yard. Book with my link and we both get a half-price mow (code ${code}):` };
}

/** Prove the Google review from their own page: the $10 goes on the open invoice, or the next one. */
function ReviewProof({ token }: { token: string }) {
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData(); form.append('screenshot', file);
      const r = await publicApi.post(`/review-claims/portal/${token}`, form);
      setMsg(r.data?.message || 'Thanks! We have your screenshot.');
    } catch (err: any) { setMsg(err.response?.data?.message || 'Could not upload that. Text it to us instead.'); }
    finally { setBusy(false); }
  }
  return (
    <div className="mt-3">
      <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm font-semibold cursor-pointer ${busy ? 'opacity-50' : ''}`}>
        <Camera className="h-4 w-4" /> {busy ? 'Uploading…' : 'Upload your review screenshot'}
        <input type="file" accept="image/*" className="hidden" onChange={pick} disabled={busy} />
      </label>
      {msg && <p className="mt-2 text-sm font-medium text-brand-800">{msg}</p>}
    </div>
  );
}

/** What they have earned, in plain words. Managing it happens in Settings. */
function RewardsSummary({ rewards, onOpenSettings }: { rewards: any; onOpenSettings: () => void }) {
  const reviewOffer = (rewards.offers || []).find((o: any) => o.key === 'review');
  const [shared, setShared] = useState(false);
  const shareCode = async () => {
    const { text, url } = shareMessage(rewards);
    if ((navigator as any).share) { try { await (navigator as any).share({ text, url }); return; } catch { /* cancelled */ } }
    try { await navigator.clipboard.writeText(`${text} ${url}`); setShared(true); setTimeout(() => setShared(false), 2500); } catch { /* ignore */ }
  };
  const [more, setMore] = useState(false);
  return (
    <Card title="Your rewards" icon={Gift} tone="brand">
      {/* One thing to do, up front; the rest one tap away. */}
      {reviewOffer?.available ? (
        <p className="text-[15px] text-gray-900 mb-3"><b>$10 off</b> for a Google review. Upload the screenshot in Settings, or on your next invoice.</p>
      ) : (
        <p className="text-[15px] text-gray-900 mb-3">Share your code. You both earn a cheaper mow.</p>
      )}
      {!more && (
        <div className="flex items-center gap-3 mb-1">
          {rewards.referral_code && (
            <button onClick={shareCode}
              className="press inline-flex items-center gap-2 text-[14px] font-black uppercase tracking-wide text-black bg-brand-600 px-4 min-h-[48px] clip-corner-sm">
              <Share2 className="h-4 w-4" aria-hidden /> {shared ? 'Copied' : 'Share my code'}
            </button>
          )}
          <button onClick={() => setMore(true)} className="text-[14px] font-bold text-brand-700 underline min-h-[48px]">See rewards</button>
        </div>
      )}
      {more && (<>
      {rewards.credits?.length > 0 ? (
        <ul className="space-y-1 mb-3">
          {rewards.credits.map((c: any) => (
            <li key={c.id} className="text-sm text-gray-900">
              <span className="font-bold">{c.kind === 'percent' ? `${c.amount}% off` : `${money(c.amount)} off`}</span> {c.label.toLowerCase()}
              <span className="text-gray-600">, comes off your next {c.applies_to === 'any' ? 'invoice' : c.applies_to}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-700 mb-3">Nothing waiting yet. Here's how to earn:</p>
      )}
      <ul className="space-y-1.5 text-sm text-gray-700">
        {reviewOffer?.available && <li>• <b>$10 off</b> for a Google review. Upload the screenshot in Settings, or on your invoice.</li>}
        <li>• <b>Half-price mow for both of you</b> when a neighbor you send has had two mows.</li>
        <li>• <b>A free mow</b> if they book a project over $1,000, or half price over $500.</li>
        <li>• <b>10% off</b> if you served. Add it in Settings.</li>
      </ul>
      {rewards.referral_code ? (
        <button onClick={shareCode} className="press mt-3 w-full text-sm font-semibold text-black bg-brand-600 hover:bg-brand-500 rounded-xl py-2">
          {shared ? 'Copied, paste it to a friend' : 'Share and earn, send your code'}
        </button>
      ) : (
        <button onClick={onOpenSettings} className="mt-3 text-sm font-semibold text-brand-700">Share your code →</button>
      )}
      </>)}
    </Card>
  );
}

function SettingsTab({ token, data, onChange, openContact, onContactOpened }: {
  token: string; data: any; onChange: () => void; openContact?: boolean; onContactOpened?: () => void;
}) {
  const { customer, properties, rewards, card } = data;
  const [copied, setCopied] = useState(false);
  const [how, setHow] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const share = async () => {
    const { text, url } = shareMessage(rewards);
    if (navigator.share) { try { await navigator.share({ text, url }); return; } catch { /* cancelled */ } }
    navigator.clipboard?.writeText(`${text} ${url}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const military = useMutation(() => publicApi.post(`/portal/${token}/military`, {}), {
    onSuccess: (r: any) => { setMsg(r.data.message); onChange(); },
    onError: () => { setMsg('Could not save that. Message us instead.'); },
  });
  const addCard = useMutation(() => publicApi.post(`/portal/${token}/card`), {
    onSuccess: (r: any) => { window.location.href = r.data.url; },
    onError: (e: any) => { setMsg(e.response?.data?.message || 'Could not open the card form.'); },
  });
  const removeCard = useMutation(() => publicApi.delete(`/portal/${token}/card`), {
    onSuccess: () => { setMsg('Card removed.'); onChange(); },
  });
  // Autopay is their choice (owner, Sep 28 2026): the card pays each month's bill when it is ready, or it just sits there for paying by hand.
  const autopay = useMutation((on: boolean) => publicApi.post(`/portal/${token}/autopay`, { on }), {
    onSuccess: (r: any) => { setMsg(r.data.message); onChange(); },
    onError: (e: any) => { setMsg(e.response?.data?.message || 'Could not change that.'); },
  });

  return (
    <>
      <Card title="Credit card on file" icon={CreditCard} id="portal-card">
        {card?.on_file ? (
          <>
            <p className="text-sm text-gray-800 font-medium flex items-center gap-1.5"><Check className="h-4 w-4 text-green-600" /> A credit card is saved for your invoices.</p>
            <div className="mt-2 rounded-lg border border-gray-200 p-3 flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">Autopay {card?.autopay ? 'is on' : 'is off'}</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  {card?.autopay
                    ? "Each month's bill is charged to this card when it's ready, and you get a receipt. Nothing else is ever charged."
                    : "We send the bill and you pay it when it arrives. Turn autopay on and the card pays it for you."}
                </p>
              </div>
              <button onClick={() => autopay.mutate(!card?.autopay)} disabled={autopay.isLoading}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold border ${card?.autopay ? 'border-gray-300 text-gray-700' : 'bg-gray-900 text-white border-gray-900'} disabled:opacity-50`}>
                {autopay.isLoading ? '…' : card?.autopay ? 'Turn off' : 'Turn on'}
              </button>
            </div>
            <button onClick={() => { if (window.confirm('Remove your saved card?')) removeCard.mutate(); }}
              className="mt-2 text-sm text-gray-500 underline">Remove it</button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700">Put a credit card on file and we bill it automatically on the last day of the month for the visits done that month. No card? We invoice after each visit and you pay when it arrives.</p>
            <button onClick={() => addCard.mutate()} disabled={addCard.isLoading || !card?.available}
              className="mt-3 px-4 py-2 rounded-lg border border-gray-300 text-gray-800 text-sm font-semibold disabled:opacity-50">
              {addCard.isLoading ? 'Opening…' : 'Save a credit card'}
            </button>
            {!card?.available && <p className="text-xs text-gray-500 mt-2">Card payments are not switched on yet. Give us a call.</p>}
          </>
        )}
        <p className="text-xs text-gray-500 mt-2">Your card details go straight to Stripe. We never see or store the number.</p>
        {card?.test_mode && <p className="text-xs text-amber-700 mt-1 font-medium">Test mode. No real card will be charged yet.</p>}
      </Card>

      <Card title="Refer a neighbor" icon={Gift} tone="brand" id="portal-rewards">
        <p className="text-[15px] text-gray-800">Share your code. You both earn a cheaper mow.</p>
        <button onClick={() => setHow(v => !v)} className="mt-1 text-xs font-semibold text-brand-700">{how ? 'Hide' : 'How it works'}</button>
        {how && (
        <ul className="mt-2 space-y-1 text-sm text-gray-700">
          <li>• They enter your code on their quote when they book.</li>
          <li>• After their <b>second mow</b>, you <b>both</b> get a mow at half price.</li>
          <li>• If they book a project over <b>$500</b>, you get a half-price mow.</li>
          <li>• Over <b>$1,000</b>, yours is <b>free</b>.</li>
          <li>• They get <b>5% off</b> their project either way.</li>
        </ul>
        )}
        {rewards.referral_code && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <code className="px-3 py-2 rounded-lg bg-white border border-brand-200 font-bold tracking-wider text-brand-800">{rewards.referral_code}</code>
            <button onClick={share} className="press px-3 py-2 rounded-xl bg-brand-600 text-black text-sm font-semibold flex items-center gap-1.5">
              {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />} {copied ? 'Copied' : 'Share'}
            </button>
            {rewards.referred_count > 0 && <span className="text-xs text-gray-600">{rewards.referred_count} used so far</span>}
          </div>
        )}
      </Card>

      <Card title="Military discount" icon={ShieldCheck}>
        {rewards.military_status === 'verified' ? (
          <p className="text-sm text-green-700 font-medium">Active. 10% off your invoices. Thank you for your service.</p>
        ) : rewards.military_status === 'pending' ? (
          <p className="text-sm text-gray-700">We're verifying your service. We'll apply the 10% as soon as that's done.</p>
        ) : (
          <>
            <p className="text-sm text-gray-700">Ten percent off, for anyone who served. Tap below and we'll confirm it with you.</p>
            <button onClick={() => military.mutate()} disabled={military.isLoading}
              className="mt-3 px-4 py-2 rounded-lg border border-blue-300 text-blue-800 text-sm font-semibold disabled:opacity-50">
              {military.isLoading ? 'Sending…' : 'I served, add it'}
            </button>
          </>
        )}
      </Card>

      <Card title="Your details" icon={MapPin} id="portal-contact">
        <ul className="space-y-1 mb-2">
          {properties.filter((p: any) => !p.cancelled).map((p: any) => (
            <AddressRow key={p.id} token={token} property={p} onChange={onChange} />
          ))}
        </ul>
        <Contact token={token} customer={customer} onSaved={onChange} openNow={openContact} onOpened={onContactOpened} />
      </Card>

      {(rewards.offers || []).find((o: any) => o.key === 'review')?.available && (
        <Card title="Google review" icon={Star}>
          <p className="text-sm text-gray-700">Leave us a review and get <b>$10 off</b>. Upload the screenshot here and it comes off your open invoice, or the next one.</p>
          <ReviewProof token={token} />
        </Card>
      )}

      {msg && <p className="text-sm font-medium text-brand-800">{msg}</p>}
    </>
  );
}

/** Message the office. Lands on Michael's phone. */
function MessageSheet({ token, onClose }: { token: string; onClose: () => void }) {
  const [text, setText] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const send = useMutation(() => publicApi.post(`/portal/${token}/message`, { text }), {
    onSuccess: (r: any) => setSent(r.data.message),
    onError: (e: any) => setSent(e.response?.data?.message || `Could not send that. Please text ${DEFAULT_COMPANY.phone}.`),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Message us</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        {sent ? (
          <>
            <p className="text-sm text-gray-800">{sent}</p>
            <button onClick={onClose} className="press w-full py-2.5 rounded-xl bg-brand-600 text-black font-semibold">Done</button>
          </>
        ) : (
          <>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={4} autoFocus
              placeholder="Gate code changed, skip next week, quote for the back bed…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            <button onClick={() => send.mutate()} disabled={!text.trim() || send.isLoading}
              className="press w-full py-2.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
              {send.isLoading ? 'Sending…' : 'Send'}
            </button>
            <p className="text-xs text-gray-500 text-center">Goes straight to Michael's phone.</p>
          </>
        )}
      </div>
    </div>
  );
}

/** A wrong address gets fixed here; the office is told so the pin and route get checked. */
/**
 * "Finish setting up your account."
 *
 * The owner asked for a guide that tells a customer what to fill in (Sep 16
 * 2026). Every line says what it buys them, the gate code and the dog can be
 * typed right here, and the address is confirmed with one tap. Nothing nags:
 * when it is all done the card says so and gets out of the way.
 */
function SetupGuide({ token, setup, properties, onChange, onGoSettings }: {
  token: string; setup: any; properties: any[]; onChange: () => void; onGoSettings: (action?: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [yardNote, setYardNote] = useState('');
  const [pets, setPets] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const yard = properties.find((p: any) => !p.cancelled) || properties[0] || null;

  const saveYard = useMutation(
    async () => (await publicApi.put(`/portal/${token}/yard`, {
      property_id: yard?.id,
      yard_note: yardNote.trim() || undefined,
      pets_note: pets.trim() || undefined,
    })).data,
    { onSuccess: (d: any) => { setSaved(d?.message || 'Saved, thank you.'); setOpen(null); onChange(); } }
  );
  const confirmAddress = useMutation(
    async () => (await publicApi.post(`/portal/${token}/address/confirm`, { property_id: yard?.id })).data,
    { onSuccess: (d: any) => { setSaved(d?.message || 'Thanks.'); setOpen(null); onChange(); } }
  );

  if (!setup || !Array.isArray(setup.items)) return null;
  const items = setup.items as any[];
  if (setup.complete) {
    return (
      <Card title="Your account" icon={ShieldCheck} tone="green">
        <p className="text-sm font-semibold text-emerald-900">{setup.headline}</p>
        <p className="text-xs text-emerald-800 mt-0.5">{setup.blurb}</p>
      </Card>
    );
  }

  return (
    <Card title="Finish your account" icon={ShieldCheck} tone="brand">
      <p className="text-sm font-semibold text-gray-900">{setup.headline}</p>
      <p className="text-xs text-gray-600 mt-0.5">{setup.blurb}</p>
      <div className="mt-3 h-1.5 rounded-full bg-white/70 overflow-hidden">
        <div className="h-full bg-brand-600" style={{ width: `${setup.percent}%` }} />
      </div>
      <p className="mt-1 text-[11px] text-gray-500">{setup.done} of {setup.total} done</p>

      <ul className="mt-3 space-y-2">
        {items.map((it: any) => (
          <li key={it.key} className="rounded-xl bg-white border border-gray-200 px-3.5 py-3">
            <div className="flex items-start gap-3">
              <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${it.done ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {it.done ? <Check className="h-3.5 w-3.5" /> : <span className="text-[11px] font-bold">·</span>}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${it.done ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{it.label}</p>
                {!it.done && <p className="text-xs text-gray-500 mt-0.5">{it.why}</p>}
              </div>
              {!it.done && (
                <button
                  onClick={() => {
                    if (it.action === 'yard') { setYardNote(yard?.yard_note || ''); setPets(yard?.pets_note || ''); setOpen(open === 'yard' ? null : 'yard'); }
                    else if (it.action === 'address') setOpen(open === 'address' ? null : 'address');
                    else onGoSettings(it.action);
                  }}
                  className="text-xs font-semibold text-brand-700 flex-shrink-0">
                  {it.action === 'address' ? 'Check' : 'Add'}
                </button>
              )}
            </div>

            {!it.done && open === 'yard' && it.action === 'yard' && (
              <div className="mt-3 space-y-2">
                <input value={yardNote} onChange={e => setYardNote(e.target.value)}
                  placeholder="Gate code, which side the gate is on, where to park"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input value={pets} onChange={e => setPets(e.target.value)}
                  placeholder="Any pets? (name, where they are)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <button onClick={() => saveYard.mutate()} disabled={saveYard.isLoading || (!yardNote.trim() && !pets.trim())}
                  className="press w-full py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-50">
                  {saveYard.isLoading ? 'Saving…' : 'Save, the crew sees this'}
                </button>
              </div>
            )}

            {!it.done && open === 'address' && it.action === 'address' && yard && (
              <div className="mt-3 space-y-2">
                <p className="text-sm text-gray-900 font-medium">{[yard.address, yard.city].filter(Boolean).join(', ')}</p>
                <div className="flex gap-2">
                  <button onClick={() => confirmAddress.mutate()} disabled={confirmAddress.isLoading}
                    className="press flex-1 py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-50">
                    {confirmAddress.isLoading ? 'Saving…' : "That's right"}
                  </button>
                  <button onClick={() => onGoSettings('address')}
                    className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold">Fix it</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      {saved && <p className="mt-3 text-sm text-brand-800">{saved}</p>}
    </Card>
  );
}

function AddressRow({ token, property, onChange }: { token: string; property: any; onChange: () => void }) {
  const [editing, setEditing] = useState(false);
  const [address, setAddress] = useState(property.address || '');
  const [city, setCity] = useState(property.city || '');
  const [zip, setZip] = useState(property.zip_code || '');
  const [msg, setMsg] = useState<string | null>(null);
  const save = useMutation(() => publicApi.put(`/portal/${token}/address`, { property_id: property.id, address, city, zip_code: zip }), {
    onSuccess: (r: any) => { setMsg(r.data?.message || 'Saved'); setEditing(false); onChange(); },
    onError: (e: any) => { setMsg(e.response?.data?.message || 'Could not save that. Please text us the address instead.'); },
  });
  if (!editing) {
    return (
      <li className="text-sm text-gray-700">
        {property.address}{property.city ? `, ${property.city}` : ''}
        {property.price > 0 && <span className="text-gray-500"> · {money(property.price)} a visit</span>}
        <button onClick={() => { setMsg(null); setEditing(true); }} className="ml-2 text-brand-600 font-medium">Wrong address?</button>
        {msg && <span className="block text-xs text-brand-800 mt-0.5">{msg}</span>}
      </li>
    );
  }
  return (
    <li className="space-y-2 rounded-lg border border-gray-200 p-3">
      <input value={address} onChange={e => setAddress(e.target.value)} placeholder="Street address" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
      <div className="flex gap-2">
        <input value={city} onChange={e => setCity(e.target.value)} placeholder="City" className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        <input value={zip} onChange={e => setZip(e.target.value)} placeholder="ZIP" inputMode="numeric" className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
      </div>
      <div className="flex gap-2">
        <button onClick={() => setEditing(false)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600">Cancel</button>
        <button onClick={() => save.mutate()} disabled={save.isLoading || !address.trim()} className="press px-3 py-2 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-50">{save.isLoading ? 'Saving…' : 'Save address'}</button>
      </div>
      <p className="text-xs text-gray-500">We check the new address against the route before the next visit.</p>
    </li>
  );
}

/**
 * The customer's own phone and email.
 *
 * The owner, Sep 20 2026: "the customer can't put their email in." Three things
 * were in the way, and all three looked the same from their side:
 *   - with no email on file there was nothing asking for one, just the word
 *     "Update" after their phone number
 *   - a refused save said nothing at all — no error was ever shown, so a typo
 *     or a server that said no just did nothing
 *   - a save that worked did not refresh the page behind it, so the line still
 *     read "no email" afterwards and looked like it had failed
 */
function Contact({ token, customer, onSaved, openNow, onOpened }: {
  token: string; customer: any; onSaved?: () => void; openNow?: boolean; onOpened?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(customer.email || '');
  const [phone, setPhone] = useState(customer.phone || '');
  const [saved, setSaved] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation(() => publicApi.put(`/portal/${token}/contact`, { email, phone }), {
    onSuccess: () => {
      setProblem(null); setSaved(true); setEditing(false);
      onSaved?.();                                   // so the line below reads the new email
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e: any) => { setProblem(e?.response?.data?.message || 'That did not save. Try again in a moment.'); },
  });
  const ok = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  // Sent here by the tutorial: open the boxes and put them in front of them,
  // rather than leaving them on a tab to work out which row to tap.
  useEffect(() => {
    if (!openNow) return;
    setEditing(true);
    onOpened?.();
    setTimeout(() => document.getElementById('portal-contact')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  }, [openNow, onOpened]);

  if (!editing) {
    return (
      <div className="text-sm text-gray-500">
        <p>
          {customer.phone || 'No phone on file'}{customer.email ? ` · ${customer.email}` : ''}
          <button onClick={() => setEditing(true)} className="press ml-2 text-brand-700 font-bold min-h-[44px]">{saved ? 'Saved' : 'Update'}</button>
        </p>
        {!customer.email && (
          <button onClick={() => setEditing(true)} className="press mt-1 text-[15px] text-brand-700 font-bold underline underline-offset-2 min-h-[44px]">
            Add your email
          </button>
        )}
        {!customer.email && <p className="text-xs text-gray-500 mt-0.5">We will send receipts there. We do not send anything else.</p>}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone" inputMode="tel"
        className="w-full rounded-xl border-2 border-gray-200 px-3 min-h-[48px] text-[16px] focus:outline-none focus:border-brand-600" />
      <input value={email} onChange={e => { setEmail(e.target.value); setProblem(null); }} placeholder="Email" inputMode="email"
        type="email" autoComplete="email"
        className={`w-full rounded-xl border-2 px-3 min-h-[48px] text-[16px] focus:outline-none focus:border-brand-600 ${ok ? 'border-gray-200' : 'border-red-500'}`} />
      {!ok && <p className="text-[13px] font-semibold text-red-700">That email is missing an @ or a dot.</p>}
      {problem && <p className="text-[13px] font-semibold text-red-700">{problem}</p>}
      <div className="flex gap-2">
        <button onClick={() => { setEditing(false); setProblem(null); }} className="press px-3 py-2 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-700">Cancel</button>
        <button onClick={() => save.mutate()} disabled={save.isLoading || !ok}
          className="press px-3 py-2 rounded-xl bg-brand-600 text-black text-sm font-bold shadow-brand disabled:opacity-50">
          {save.isLoading ? 'Saving' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/**
 * The customer's own lawn program (round by round) once they are on one.
 * Plans are not sold from this page — that is the office, a bid, or the flyer.
 * An upcoming visit they cannot make slides one month (services/planTiming).
 */
function TreatmentProgram({ token, program, onChange }: { token: string; program: any; onChange: () => void }) {
  const fmt = (d?: string | null) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
  const next = (program.rounds || []).find((r: any) => !r.done && !r.skipped);
  const [err, setErr] = useState<string | null>(null);
  const move = useMutation(
    ({ id, dir }: { id: number; dir: 1 | -1 }) => publicApi.post(`/portal/${token}/program/rounds/${id}/move`, { dir }).then(r => r.data),
    {
      onSuccess: () => { setErr(null); onChange(); },
      onError: (e: any) => setErr(e?.response?.data?.message || 'Could not move that. Text us and we will sort it.'),
    }
  );
  return (
    <Card title="Your lawn program" icon={Leaf} tone="green">
      <p className="font-semibold text-gray-900">{program.name}</p>
      {next && <p className="text-sm text-emerald-800 mt-0.5">Next: {next.label} on {fmt(next.date)}. Please keep pets and kids off the grass for about two hours after we leave.</p>}
      {(program.rounds || []).some((r: any) => r.can_earlier || r.can_later) && (
        <p className="text-[13px] text-gray-700 mt-1">A date that does not work? Slide it a month. It still rides a mow.</p>
      )}
      {err && <p className="text-[13px] font-semibold text-amber-800 mt-1">{err}</p>}
      <ul className="mt-3 space-y-1.5">
        {(program.rounds || []).map((r: any, i: number) => (
          <li key={r.id || i} className={`text-sm ${r.skipped ? 'text-gray-500 line-through' : r.done ? 'text-gray-500' : 'text-gray-800'}`}>
            <div className="flex items-center justify-between gap-2">
              <span>{r.done ? 'Done: ' : ''}{r.label}</span>
              <span className="text-xs flex-shrink-0">{fmt(r.date)}{r.price > 0 ? ` · $${r.price.toFixed(2)}` : ''}</span>
            </div>
            {!r.done && !r.skipped && r.id && (r.can_earlier || r.can_later) && (
              <div className="flex items-center gap-2 mt-1">
                <button type="button" disabled={!r.can_earlier || move.isLoading} onClick={() => move.mutate({ id: r.id, dir: -1 })}
                  className="press px-3.5 min-h-[40px] rounded-xl border-2 border-emerald-300 bg-white text-emerald-800 text-sm font-bold disabled:opacity-30">Earlier</button>
                <button type="button" disabled={!r.can_later || move.isLoading} onClick={() => move.mutate({ id: r.id, dir: 1 })}
                  className="press px-3.5 min-h-[40px] rounded-xl border-2 border-emerald-300 bg-white text-emerald-800 text-sm font-bold disabled:opacity-30">Later</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Help: the two ways to reach a person, and the in-app message. Same number as the footer. */
function HelpSheet({ company, onClose, onMessage }: { company: { name: string; phone: string; phone_raw: string }; onClose: () => void; onMessage: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Need a hand?</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        <p className="text-sm text-gray-600">Michael answers texts fastest. Anything about a visit, a bill or the yard, just ask.</p>
        <a href={`sms:${company.phone_raw}`} className="press w-full py-3 rounded-xl bg-brand-600 text-black font-semibold flex items-center justify-center gap-2"><MessageSquare className="h-4 w-4" /> Text {company.phone}</a>
        <a href={`tel:${company.phone_raw}`} className="w-full py-3 rounded-xl border border-gray-300 text-gray-800 font-semibold flex items-center justify-center gap-2"><Phone className="h-4 w-4" /> Call {company.phone}</a>
        <button onClick={onMessage} className="w-full py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold">Send a message from here</button>
        <p className="text-xs text-gray-500 text-center">Your link never expires. Save this page to your home screen.</p>
      </div>
    </div>
  );
}
